import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSessionRepairList } from '../../research/packages/session-repair';
import type { SessionRepairLiteSession } from '../../research/packages/session-repair/types';
import type { GroupSessionRow } from '../utils/sortGroupSessions';
import { prefetchSessionLiteMetadata } from '../queries/prefetchSessionLiteMetadata';
import { useSessionGroupQuery } from '../queries/useSessionGroupQuery';

const toLiteSession = (
  sessionId: number,
  item?: {
    patientCode?: string | null;
    started_at?: string | Date | null;
    created_at?: string | Date | null;
    updated_at?: string | Date | null;
    scenario?: string;
    hasHrData?: boolean;
    hasHrDataRepaired?: boolean;
  }
): SessionRepairLiteSession => ({
  id: sessionId,
  patientCode: item?.patientCode,
  started_at: item?.started_at ?? null,
  created_at: item?.created_at ?? '',
  updated_at: item?.updated_at ?? null,
  scenario: item?.scenario ?? '',
  hasHrData: item?.hasHrData ?? false,
  hasHrDataRepaired: item?.hasHrDataRepaired ?? false,
});

export const useSessionGroupSessions = (
  groupId: string | undefined,
  userId: string | undefined
) => {
  const queryClient = useQueryClient();
  const {
    data: group,
    isLoading: isLoadingGroup,
    error: groupError,
  } = useSessionGroupQuery(groupId);
  const [liteSessions, setLiteSessions] = useState<SessionRepairLiteSession[]>(
    []
  );
  const [isLoadingLite, setIsLoadingLite] = useState(false);
  const [liteError, setLiteError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const loadLiteSessions = useCallback(
    async (options?: { force?: boolean }) => {
      if (!group || !userId) {
        setLiteSessions([]);
        setLiteError(null);
        return;
      }

      setIsLoadingLite(true);
      setLiteError(null);

      try {
        const liteById = await prefetchSessionLiteMetadata(
          queryClient,
          userId,
          group.sessionIds,
          options
        );

        setLiteSessions(
          group.sessionIds.map(sessionId =>
            toLiteSession(sessionId, liteById.get(sessionId))
          )
        );
      } catch (error) {
        setLiteError(
          error instanceof Error
            ? error.message
            : 'Impossibile caricare le sessioni del gruppo.'
        );
        setLiteSessions(
          group.sessionIds.map(sessionId => toLiteSession(sessionId))
        );
      } finally {
        setIsLoadingLite(false);
      }
    },
    [group, queryClient, userId]
  );

  useEffect(() => {
    void loadLiteSessions({ force: reloadToken > 0 });
  }, [loadLiteSessions, reloadToken]);

  const reloadSessions = useCallback(() => {
    setReloadToken(current => current + 1);
  }, []);

  const { rows: repairRows } = useSessionRepairList(liteSessions);

  const tableRows = useMemo<GroupSessionRow[]>(
    () =>
      repairRows.map(row => ({
        sessionId: row.sessionId,
        patientCode: row.patientCode,
        sessionDate: row.sessionDate,
        hasHrData: row.hasHrData,
        repairStatus: row.repairStatus,
        hrEventCount: row.eventCount ?? (row.hasHrData ? -1 : 0),
        status:
          row.repairStatus === 'analyzing'
            ? 'loading'
            : row.repairStatus === 'analysis_failed'
              ? 'error'
              : 'ready',
        duplicateCount: row.duplicateCount,
        gapCount: row.gapCount,
      })),
    [repairRows]
  );

  const needsRepairSessionIds = useMemo(
    () =>
      tableRows
        .filter(row => row.repairStatus === 'needs_repair')
        .map(row => row.sessionId),
    [tableRows]
  );

  return {
    group,
    tableRows,
    needsRepairSessionIds,
    reloadSessions,
    isLoading: isLoadingGroup || isLoadingLite,
    error: groupError ?? liteError,
  };
};
